// src/pages/InviterDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import {PersonRemove} from "@mui/icons-material";

interface Action {
  action_id: string;
  action_type: string;
  account_name: string;
  user_id: number;
  reason?: string;
  error_message?: string;
  created_at: string;
}

interface Detail {
  campaign_id: string;
  name: string;
  account_names?: string[]; // может отсутствовать
  status: string;
  target_chat_name?: string;
  total_users: number;
  invited_count: number;
  skipped_count: number;
  failed_count: number;
  skip_reasons: Record<string, number>;
  error_reasons: Record<string, number>;
  accounts_stats: Record<string, any>;
  last_invite?: any;
  last_error?: any;
  started_at?: string;
  last_activity_at?: string;
  completed_at?: string;
  uptime_seconds: number;
  campaign_day?: number;
  actions: Action[];
}

const InviterDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();
  const inviteActionTypes = {
    invited: {
      label: 'Пользователь приглашён',
      icon: <PersonAddIcon fontSize="small" color="success"/>
    },
    invited_as_admin: {
      label: 'Приглашён как администратор',
      icon: <PersonAddIcon fontSize="small" color="primary"/>
    },
    skipped: {
      label: 'Пропущен',
      icon: <SkipNextIcon fontSize="small" color="warning"/>
    },
    error: {
      label: 'Ошибка',
      icon: <ErrorIcon fontSize="small" color="error"/>
    },
    demoted: {
      label: 'Забраны админ-права',
      icon: <PersonRemove fontSize='small' color='warning'/>
    }
  };
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      navigate('/inviter');
      return;
    }
  }, [jobId, navigate]);

  const fetchData = async () => {
    if (!jobId) return;

    try {
      const [statsRes, actionsRes] = await Promise.all([
        api.get(`/inviter/status/${jobId}`),
        api.get(`/inviter/actions/${jobId}`)
      ]);

      setData({
        ...statsRes.data,
        account_names: statsRes.data.account_names || [], // защита
        actions: actionsRes.data || []
      });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Кампания не найдена или удалена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [jobId]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики инвайтера...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Кампания не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inviter')}>
          Назад к инвайтеру
        </Button>
      </Container>
    );
  }

  const uptime = data.uptime_seconds || 0;
  const hours = Math.floor(uptime / 3600);
  const minutes = Math.floor((uptime % 3600) / 60);
  const seconds = uptime % 60;

  const progress = data.total_users > 0 ? (data.invited_count / data.total_users) * 100 : 0;

  // Безопасно берём количество аккаунтов из accounts_stats, если account_names нет
  const accountsCount = data.account_names?.length ?? Object.keys(data.accounts_stats || {}).length;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inviter')} sx={{ mb: 4 }}>
        Назад к инвайтеру
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Инвайтер #{jobId.slice(0, 8)} — {data.name}
        </Typography>

        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунтов: <strong>{accountsCount}</strong> · Целевой чат: <strong>{data.target_chat_name || '—'}</strong> ·
          <AccessTimeIcon sx={{ verticalAlign: 'middle', mx: 1 }} />
          Время работы: <strong>{hours}ч {minutes}м {seconds}с</strong>
          {data.campaign_day && ` · День кампании: ${data.campaign_day}`}
        </Typography>

        {/* Основные карточки */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {data.invited_count}
                </Typography>
                <Typography color="text.secondary">Приглашено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {data.skipped_count}
                </Typography>
                <Typography color="text.secondary">Пропущено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#DC2626">
                  {data.failed_count}
                </Typography>
                <Typography color="text.secondary">Ошибок</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#3B82F6">
                  {progress.toFixed(0)}%
                </Typography>
                <Typography color="text.secondary">Прогресс</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Пропуски */}
        {Object.keys(data.skip_reasons || {}).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#F59E0B" fontWeight={600} gutterBottom>
              Причины пропусков
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(data.skip_reasons).map(([reason, count]) => (
                <Chip
                  key={reason}
                  icon={<SkipNextIcon />}
                  label={`${reason}: ${count}`}
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Ошибки */}
        {Object.keys(data.error_reasons || {}).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#DC2626" fontWeight={600} gutterBottom>
              Ошибки
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(data.error_reasons).map(([reason, count]) => (
                <Chip
                  key={reason}
                  icon={<ErrorIcon />}
                  label={`${reason}: ${count}`}
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Последняя ошибка */}
        {data.last_error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <strong>Последняя ошибка:</strong> {data.last_error.error_message || data.last_error.reason || 'Неизвестная ошибка'}
            {data.last_error.account_name && ` (аккаунт: ${data.last_error.account_name})`}
            <br />
            <Typography component="span" fontSize="0.85rem">
              {data.last_error.created_at ? new Date(data.last_error.created_at).toLocaleString('ru-RU') : '—'}
            </Typography>
          </Alert>
        )}

        {/* Последние действия */}
        {(data.actions || []).length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Последние действия
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Время</strong></TableCell>
                    <TableCell><strong>Тип</strong></TableCell>
                    <TableCell><strong>Аккаунт</strong></TableCell>
                    <TableCell><strong>Пользователь</strong></TableCell>
                    <TableCell><strong>Причина</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.actions.slice(0, 20).map(a => (
                    <TableRow key={a.action_id}>
                      <TableCell>{new Date(a.created_at).toLocaleString('ru-RU')}</TableCell>
                      <TableCell>
  {inviteActionTypes[a.action_type as keyof typeof inviteActionTypes]?.icon}
  {' '}
  {inviteActionTypes[a.action_type as keyof typeof inviteActionTypes]?.label || a.action_type}
</TableCell>
                      <TableCell>{a.account_name}</TableCell>
                      <TableCell>{a.user_id}</TableCell>
                      <TableCell sx={{ color: a.reason ? '#DC2626' : 'inherit' }}>
                        {a.reason || a.error_message || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Время */}
        <Box sx={{ p: 4, bgcolor: '#F9FAFB', borderRadius: 2 }}>
          <Typography variant="h6" color="#1E40AF" gutterBottom>
            Время
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Запущен</Typography>
              <Typography fontWeight={600}>{data.started_at ? new Date(data.started_at).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Последняя активность</Typography>
              <Typography fontWeight={600}>{data.last_activity_at ? new Date(data.last_activity_at).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Завершён</Typography>
              <Typography fontWeight={600} color={data.completed_at ? '#10B981' : 'inherit'}>
                {data.completed_at ? new Date(data.completed_at).toLocaleString('ru-RU') : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default InviterDetail;